import {
  AlertCircle,
  AlertTriangle,
  CheckCircle2,
  ClipboardList,
  Clock,
  FileCheck,
  FileX,
  Play,
  UserCog,
  UserX,
  type LucideIcon,
} from "lucide-react";
import {
  format,
  subMonths,
  startOfDay,
  endOfDay,
  startOfMonth,
  endOfMonth,
  startOfWeek,
  endOfWeek,
} from "date-fns";
import type { Locale } from "date-fns";
import type { DateRange } from "react-day-picker";
import { isDateOverdue } from "@/lib/dateUtils";
import type { DashboardStats } from "@/hooks/useDashboardQuery";

/**
 * Reine Helfer für die (filter-gekoppelte) Standard-Dashboard-Seite (TES-848).
 *
 * Extrahiert aus dem Dashboard.tsx-Monolithen — VERHALTENSERHALTEND: identische
 * Status-Ableitung, Datums-Presets, Ziel-Routen, StatCard-Definitionen (testIds,
 * Farben, Werte, Rollen-Gating) und Filter-Chip-Labels wie zuvor inline.
 *
 * STRIKT real-schema-treu (D-CHK-015): defect_status (open/in_progress/resolved/
 * closed/cancelled — KEIN "verified") und inspections.status/due_date.
 */

export type InspectionStatus = "pending" | "in_progress" | "completed" | "overdue";

/** Abgeleiteter Anzeige-Status einer Prüfung (überfällig nur visuell). */
export const getDisplayStatus = (inspection: {
  status: string;
  due_date: string | null;
}): InspectionStatus => {
  if (inspection.status === "completed") {
    return inspection.status;
  }
  if (isDateOverdue(inspection.due_date)) {
    return "overdue";
  }
  return inspection.status as InspectionStatus;
};

export type DatePreset = "all" | "today" | "week" | "month" | "3months" | "year" | "custom";

/**
 * Berechnet den DateRange für ein Preset. Für 'custom' (default-Zweig) wird der
 * aktuelle Range unverändert zurückgegeben (Verhalten wie zuvor inline).
 */
export const getDateRangeFromPreset = (
  preset: DatePreset,
  dateLocale: Locale,
  currentRange: DateRange | undefined,
  now: Date = new Date(),
): DateRange | undefined => {
  switch (preset) {
    case "all":
      return undefined;
    case "today":
      return { from: startOfDay(now), to: endOfDay(now) };
    case "week":
      return {
        from: startOfWeek(now, { locale: dateLocale }),
        to: endOfWeek(now, { locale: dateLocale }),
      };
    case "month":
      return { from: startOfMonth(now), to: endOfMonth(now) };
    case "3months":
      return { from: subMonths(now, 3), to: now };
    case "year":
      return { from: subMonths(now, 12), to: now };
    default:
      return currentRange;
  }
};

type Translate = (key: string) => string;

/**
 * Baut die Route `${base}?<params>` aus dem Filter-Status, Assignee und
 * Datums-Range. Geteilte Basis von buildDefectsRoute/buildInspectionsRoute —
 * identisches Verhalten wie zuvor in beiden Buildern inline.
 */
function buildFilteredRoute(opts: {
  base: "/defects" | "/inspections";
  status?: string;
  fallbackStatusFilter: string;
  assignedToFilter: string;
  dateRange: DateRange | undefined;
  severity?: string;
}): string {
  const { base, status, fallbackStatusFilter, assignedToFilter, dateRange, severity } = opts;
  const params = new URLSearchParams();

  if (status) {
    params.set("status", status);
  } else if (fallbackStatusFilter !== "all") {
    params.set("status", fallbackStatusFilter);
  }

  if (severity) {
    params.set("severity", severity);
  }

  if (assignedToFilter !== "all") {
    params.set("assignee", assignedToFilter);
  }

  if (dateRange?.from) {
    params.set("dateFrom", format(dateRange.from, "yyyy-MM-dd"));
    if (dateRange.to) {
      params.set("dateTo", format(dateRange.to, "yyyy-MM-dd"));
    }
  }

  const queryString = params.toString();
  return queryString ? `${base}?${queryString}` : base;
}

/**
 * Baut die Ziel-Route für die Mängel-Navigation aus den aktiven Filtern.
 * Reine Funktion (kein navigate) — identisches Verhalten wie zuvor inline.
 */
export function buildDefectsRoute(opts: {
  status?: string;
  severity?: string;
  defectStatusFilter: string;
  assignedToFilter: string;
  dateRange: DateRange | undefined;
}): string {
  return buildFilteredRoute({
    base: "/defects",
    status: opts.status,
    severity: opts.severity,
    fallbackStatusFilter: opts.defectStatusFilter,
    assignedToFilter: opts.assignedToFilter,
    dateRange: opts.dateRange,
  });
}

/**
 * Baut die Ziel-Route für die Prüfungs-Navigation aus den aktiven Filtern.
 * Reine Funktion (kein navigate) — identisches Verhalten wie zuvor inline.
 */
export function buildInspectionsRoute(opts: {
  status?: string;
  inspectionStatusFilter: string;
  assignedToFilter: string;
  dateRange: DateRange | undefined;
}): string {
  return buildFilteredRoute({
    base: "/inspections",
    status: opts.status,
    fallbackStatusFilter: opts.inspectionStatusFilter,
    assignedToFilter: opts.assignedToFilter,
    dateRange: opts.dateRange,
  });
}

export interface DashboardStatCardDef {
  name: string;
  value: number;
  icon: LucideIcon;
  color: string;
  bgColor: string;
  testId: string;
  onClick: () => void;
}

interface RoleFlags {
  isQualitaetsmanager: boolean;
  isTester: boolean;
  isAdmin: boolean;
}

/** Prüfungs-Kacheln der Standard-Dashboard-Seite (filter-gekoppelte Routen). */
export function buildInspectionCards(opts: {
  stats: DashboardStats | null | undefined;
  inspectionsOpen: number;
  roles: RoleFlags;
  t: Translate;
  navigate: (to: string) => void;
  routeFor: (status?: string) => string;
}): DashboardStatCardDef[] {
  const { stats, inspectionsOpen, roles, t, navigate, routeFor } = opts;
  const { isQualitaetsmanager, isTester, isAdmin } = roles;
  if (!stats) return [];

  return [
    {
      name: t("common.total"),
      value: stats.totalInspections,
      icon: ClipboardList,
      color: "text-foreground",
      bgColor: "bg-muted",
      testId: "inspection-tile-total",
      onClick: () => navigate(routeFor()),
    },
    {
      name: t("dashboard.openAll"),
      value: inspectionsOpen,
      icon: AlertCircle,
      color: "text-warning-foreground",
      bgColor: "bg-warning/15 dark:bg-warning/20",
      testId: "inspection-tile-open",
      onClick: () => navigate(routeFor("open")),
    },
    {
      name: t("dashboard.notStarted"),
      value: stats.inspectionsNotStarted,
      icon: FileCheck,
      color: "text-warning",
      bgColor: "bg-warning/10",
      testId: "inspection-tile-pending",
      onClick: () => navigate(routeFor("pending")),
    },
    {
      name: t("dashboard.inProgress"),
      value: stats.inspectionsActive,
      icon: Play,
      color: "text-primary",
      bgColor: "bg-primary/10",
      testId: "inspection-tile-in_progress",
      onClick: () => navigate(routeFor("in_progress")),
    },
    {
      name: t("dashboard.overdue"),
      value: stats.inspectionsOverdue,
      icon: Clock,
      color: "text-destructive",
      bgColor: "bg-destructive/10",
      testId: "inspection-tile-overdue",
      onClick: () => navigate(routeFor("overdue")),
    },
    {
      name: t("dashboard.completed"),
      value: stats.inspectionsCompleted,
      icon: CheckCircle2,
      color: "text-success",
      bgColor: "bg-success/10",
      testId: "inspection-tile-completed",
      onClick: () => navigate(routeFor("completed")),
    },
    {
      name: t("dashboard.cancelled"),
      value: stats.inspectionsCancelled,
      icon: FileX,
      color: "text-muted-foreground",
      bgColor: "bg-muted",
      testId: "inspection-tile-cancelled",
      onClick: () => navigate(routeFor("cancelled")),
    },
    ...(isQualitaetsmanager || isTester
      ? [
          {
            name: t("dashboard.unassigned"),
            value: stats.inspectionsUnassigned,
            icon: UserX,
            color: "text-info",
            bgColor: "bg-info/10",
            testId: "inspection-tile-unassigned",
            onClick: () => navigate(routeFor("unassigned")),
          },
        ]
      : []),
    ...(isQualitaetsmanager || isAdmin
      ? [
          {
            name: t("inspections.tabs.unavailableAssignee"),
            value: stats.inspectionsUnavailableAssignee,
            icon: UserCog,
            color: "text-warning",
            bgColor: "bg-warning/10",
            testId: "inspection-tile-unavailable",
            onClick: () => navigate("/inspections?status=unavailableAssignee"),
          },
        ]
      : []),
  ];
}

/** Mängel-Kacheln der Standard-Dashboard-Seite (filter-gekoppelte Routen). */
export function buildDefectCards(opts: {
  stats: DashboardStats | null | undefined;
  defectsOpen: number;
  roles: RoleFlags;
  t: Translate;
  navigate: (to: string) => void;
  routeFor: (status?: string) => string;
}): DashboardStatCardDef[] {
  const { stats, defectsOpen, roles, t, navigate, routeFor } = opts;
  const { isQualitaetsmanager, isTester, isAdmin } = roles;
  if (!stats) return [];

  return [
    {
      name: t("common.total"),
      value: stats.totalDefects,
      icon: AlertTriangle,
      color: "text-foreground",
      bgColor: "bg-muted",
      testId: "defect-tile-total",
      onClick: () => navigate(routeFor()),
    },
    {
      name: t("dashboard.openAll"),
      value: defectsOpen,
      icon: AlertCircle,
      color: "text-warning-foreground",
      bgColor: "bg-warning/15 dark:bg-warning/20",
      testId: "defect-tile-open",
      onClick: () => navigate(routeFor("openAll")),
    },
    {
      name: t("dashboard.notStarted"),
      value: stats.defectsNotStarted,
      icon: AlertTriangle,
      color: "text-warning",
      bgColor: "bg-warning/10",
      testId: "defect-tile-pending",
      onClick: () => navigate(routeFor("open")),
    },
    {
      name: t("dashboard.inProgress"),
      value: stats.defectsActive,
      icon: Play,
      color: "text-primary",
      bgColor: "bg-primary/10",
      testId: "defect-tile-in_progress",
      onClick: () => navigate(routeFor("in_progress")),
    },
    {
      name: t("dashboard.overdue"),
      value: stats.defectsOverdue,
      icon: Clock,
      color: "text-destructive",
      bgColor: "bg-destructive/10",
      testId: "defect-tile-overdue",
      onClick: () => navigate(routeFor("overdue")),
    },
    ...(isQualitaetsmanager
      ? [
          {
            name: t("dashboard.toVerify"),
            value: stats.defectsToVerify,
            icon: FileCheck,
            color: "text-info",
            bgColor: "bg-info/10",
            testId: "defect-tile-resolved",
            onClick: () => navigate(routeFor("verification")),
          },
        ]
      : []),
    {
      name: t("dashboard.completed"),
      value: stats.defectsCompleted,
      icon: CheckCircle2,
      color: "text-success",
      bgColor: "bg-success/10",
      testId: "defect-tile-completed",
      onClick: () => navigate(routeFor("completed")),
    },
    {
      name: t("dashboard.cancelled"),
      value: stats.defectsCancelled,
      icon: FileX,
      color: "text-muted-foreground",
      bgColor: "bg-muted",
      testId: "defect-tile-cancelled",
      onClick: () => navigate(routeFor("cancelled")),
    },
    ...(isQualitaetsmanager || isTester
      ? [
          {
            name: t("dashboard.unassigned"),
            value: stats.defectsUnassigned,
            icon: UserX,
            color: "text-info",
            bgColor: "bg-info/10",
            testId: "defect-tile-unassigned",
            onClick: () => navigate(routeFor("unassigned")),
          },
        ]
      : []),
    ...(isQualitaetsmanager || isAdmin
      ? [
          {
            name: t("defects.tabs.unavailableAssignee"),
            value: stats.defectsUnavailableAssignee,
            icon: UserCog,
            color: "text-warning",
            bgColor: "bg-warning/10",
            testId: "defect-tile-unavailable",
            onClick: () => navigate("/defects?status=unavailableAssignee"),
          },
        ]
      : []),
  ];
}

/** Label für den Prüfungs-Status-Filter-Chip (identische Map wie zuvor inline). */
export function resolveInspectionChipLabel(filter: string, t: Translate): string {
  switch (filter) {
    case "openAll":
      return t("dashboard.openAll");
    case "pending":
      return t("dashboard.notStarted");
    case "in_progress":
      return t("dashboard.inProgress");
    case "completed":
      return t("dashboard.completed");
    case "cancelled":
      return t("dashboard.cancelled");
    case "overdue":
      return t("dashboard.overdue");
    case "unassigned":
      return t("dashboard.unassigned");
    default:
      return filter;
  }
}

/** Label für den Mängel-Status-Filter-Chip (identische Map wie zuvor inline). */
export function resolveDefectChipLabel(filter: string, t: Translate): string {
  switch (filter) {
    case "openAll":
      return t("dashboard.openAll");
    case "open":
      return t("dashboard.notStarted");
    case "in_progress":
      return t("dashboard.inProgress");
    case "resolved":
      return t("dashboard.toVerify");
    case "closed":
      return t("dashboard.completed");
    case "cancelled":
      return t("dashboard.cancelled");
    case "overdue":
      return t("dashboard.overdue");
    case "unassigned":
      return t("dashboard.unassigned");
    default:
      return filter;
  }
}
